import Rollbar from 'rollbar'

import { ENV } from '@/config'

const ROLLBAR_TOKEN = typeof window === 'undefined' ? process.env.ROLLBAR_SERVER_TOKEN : process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN
// For cases where Rollbar needs to be executed
// on development environment, set ROLLBAR_ENABLED to true or type "rollbar.configure({ enabled: true })"
// in your browser console.
const ROLLBAR_ENABLED = !['development', 'test'].includes(ENV)

const resizeObserverLoopLimitRegEx = /ResizeObserver loop limit exceeded/gi

const config = {
  enabled: ROLLBAR_ENABLED,
  accessToken: ROLLBAR_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  hostSafeList: ['neat.com'],
  checkIgnore: (isUncaught, args) => {
    const [message, err] = args
    const messages = [message, err?.message, err?.stack]
    const shouldIgnore = !!(messages.some(v => resizeObserverLoopLimitRegEx.test(v)) && isUncaught)
    return shouldIgnore
  },
  payload: {
    environment: ENV,
    appBuild: process.env.NEXT_PUBLIC_BUILD_TAG,
    client: {
      javascript: {
        source_map_enabled: true,
        guess_uncaught_frames: true,
        code_version: process.env.NEXT_PUBLIC_BUILD_TAG,
      },
    },
  },
}

const Instance = Rollbar.init(config)

// Rollbar's `createItem` sorts arguments by type, so the error and the
// optional context object are passed as separate arguments; a falsy one is
// dropped rather than forwarded.
export const sendError = (error, message, context) => {
  if (!ROLLBAR_ENABLED) return
  Instance.error(message || error.message, ...[error, context].filter(Boolean))
}

export const sendWarning = (error, message, context) => {
  if (!ROLLBAR_ENABLED) return
  Instance.warning(message || error.message, ...[error, context].filter(Boolean))
}

export const trackSignUpStarted = (data) => {
  if (!data || !ROLLBAR_ENABLED) return

  Instance.info('Sign Up Started', {
    utmCampaign: data.utmCampaign,
    utmSource: data.utmSource,
    utmMedium: data.utmMedium,
    sku: data.sku,
    coupon: data.coupon,
    testsShown: data.testsShown,
  })
}

export const trackSignUpStep = (stepName, user, data) => {
  if (!data || !user || !ROLLBAR_ENABLED) return

  Instance.info(`Sign Up Step Started: ${stepName}`, {
    person: {
      ...(user.neatUserId ? { id: user.neatUserId } : {}),
      email: user.email,
      username: `${user.firstName} ${user.lastName}`,
    },
    ...data,
  })
}

/**
 * An event that is not a step: sent under its own name, with the same
 * person/data shape as a step event. `trackSignUpStep` prefixes its name and
 * must keep doing so -- the dashboards key on the prefixed strings.
 */
export const trackSignUpEvent = (eventName, user, data) => {
  if (!data || !user || !ROLLBAR_ENABLED) return

  Instance.info(eventName, {
    person: {
      ...(user.neatUserId ? { id: user.neatUserId } : {}),
      email: user.email,
      username: `${user.firstName} ${user.lastName}`,
    },
    ...data,
  })
}

export const trackSignUpCompleted = (user, data) => {
  if (!data || !user || !ROLLBAR_ENABLED) return

  Instance.info('Sign Up Completed', {
    person: {
      ...(user.neatUserId ? { id: user.neatUserId } : {}),
      email: user.email,
      username: `${user.firstName} ${user.lastName}`,
    },
    ...data,
  })
}

export default Instance
